import { useCallback } from "react";
import * as DocumentPicker from "expo-document-picker";
import { View, type StyleProp, type ViewStyle } from "react-native";
import {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";

type Props = {
  onOpenDocuments: (files: SelectedFile[]) => void;
  primaryLabel?: string;
  secondaryLabel?: string;
  style?: StyleProp<ViewStyle>;
};

/**
 * Starts the shared medical-document flow directly from a source sheet.
 *
 * The flow uploads and completes the file, extracts every result in it, and
 * stores each resulting test row with the same attachment as its proof.
 */
export function TestResultDocumentAction({
  onOpenDocuments,
  primaryLabel,
  secondaryLabel,
  style,
}: Props) {
  const handlePick = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        onOpenDocuments(
          result.assets.map((file) => ({
            name: file.name,
            uri: file.uri,
            mimeType: resolveUploadContentType({
              name: file.name,
              uri: file.uri,
              mimeType: file.mimeType,
            }),
            size: file.size ?? undefined,
          })),
        );
      }
    } catch {
      // The shared picker owns error presentation after a file is selected.
    }
  }, [onOpenDocuments]);

  return (
    <View style={[{ marginBottom: 20 }, style]}>
      <DocumentFileDropZone
        compact
        onPick={handlePick}
        onFilesSelected={onOpenDocuments}
        primaryLabel={primaryLabel}
        secondaryLabel={secondaryLabel}
      />
    </View>
  );
}
